import { useEffect, useMemo, useState } from "react";
import { addDoc, collection, onSnapshot, query, where } from "firebase/firestore";
import { editStamp } from "../lib/editStamp";
import { Link } from "wouter";
import { paths, type Coverage, type Customer, type Job, type JobKind } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, Select, cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { textMatches } from "../lib/filter";
import { useAuth } from "../hooks/useAuth";
import { useJobTypes } from "../hooks/useJobTypes";

// Visits, job walks and warranty claims: child jobs of this job. They
// schedule, route and clock like any job — this card is where they're born
// and where their progress reads at a glance.

type Row = Job & { id: string };

const KIND_LABEL: Record<string, string> = {
  visit: "Visit",
  job_walk: "Job walk",
  warranty: "Warranty claim",
};

const COVERAGE_LABEL: Record<Coverage, string> = {
  builder: "Builder pays (workmanship window)",
  manufacturer: "Manufacturer part (labor per policy)",
  homeowner: "Homeowner pays (out of warranty)",
  goodwill: "Goodwill (we absorb it)",
};

export function JobChildren({ tenantId, jobId, parent }: { tenantId: string; jobId: string; parent: Job }) {
  const { user } = useAuth();
  const [children, setChildren] = useState<Row[]>([]);
  const [adding, setAdding] = useState<JobKind | null>(null);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("08:00");
  const [coverage, setCoverage] = useState<Coverage>("homeowner");
  const [billToSearch, setBillToSearch] = useState("");
  const [billToId, setBillToId] = useState<string | null>(null);
  const [customers, setCustomers] = useState<Array<{ id: string; name: string }>>([]);
  const [busy, setBusy] = useState(false);
  const jobTypes = useJobTypes(tenantId);
  const [visitType, setVisitType] = useState("");
  const [visitNote, setVisitNote] = useState("");
  const money = (c: number) => (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
  const visitsTotal = children.reduce((s, c) => s + (c.workTotalCents ?? 0), 0);

  useEffect(() => {
    return onSnapshot(
      query(collection(db, paths.tenantJobs(tenantId)), where("parentJobId", "==", jobId)),
      (snap) => setChildren(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Job) }))),
    );
  }, [tenantId, jobId]);

  // Customers load only while picking a bill-to (warranty claims).
  useEffect(() => {
    if (adding !== "warranty") return;
    return onSnapshot(collection(db, paths.tenantCustomers(tenantId)), (snap) =>
      setCustomers(
        snap.docs
          .filter((d) => !(d.data() as Customer).archivedAt)
          .map((d) => ({ id: d.id, name: (d.data() as Customer).displayName })),
      ),
    );
  }, [tenantId, adding]);

  const billToMatches = useMemo(
    () => (billToSearch.trim() ? customers.filter((c) => textMatches(billToSearch, c.name)).slice(0, 8) : []),
    [customers, billToSearch],
  );

  async function create() {
    if (!adding || !user) return;
    setBusy(true);
    try {
      const startAt = date ? new Date(`${date}T${time}`).getTime() : null;
      const child: Job = {
        title:
          adding === "warranty"
            ? `Warranty: ${visitType ? `${visitType} - ` : ""}${parent.title}`.slice(0, 120)
            : `${KIND_LABEL[adding]}: ${visitType ? `${visitType} - ` : ""}${parent.title}`.slice(0, 120),
        ...(visitType ? { jobType: visitType } : {}),
        customerId: parent.customerId,
        locationId: null,
        status: "scheduled",
        kind: adding,
        parentJobId: jobId,
        departmentId: parent.departmentId ?? null,
        projectId: parent.projectId ?? null,
        billToCustomerId: adding === "warranty" ? billToId : null,
        coverage: adding === "warranty" ? coverage : null,
        startAt,
        endAt: startAt !== null ? startAt + 2 * 3600_000 : null,
        assignedUids: [],
        address: parent.address,
        lat: parent.lat,
        lng: parent.lng,
        note: visitNote.trim(),
        poNumber: null,
        sequence: null,
        plannedEtaAt: null,
        serviceMinutes: null,
        geocodeTriedAt: null,
        doneAt: null,
        signedOffAt: null,
        signedOffBy: null,
        createdByUid: user.uid,
        createdAt: Date.now(),
        ...editStamp(user),
      };
      await addDoc(collection(db, paths.tenantJobs(tenantId)), child);
      setAdding(null);
      setVisitType("");
      setVisitNote("");
      setDate("");
      setBillToId(null);
      setBillToSearch("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <CardTitle className="mb-0">
          Visits &amp; claims ({children.length})
          {visitsTotal ? (
            <span className="ml-2 text-xs font-normal text-steel">
              {money(visitsTotal)} logged on visits · {money(visitsTotal + (parent.workTotalCents ?? 0))} with this job
            </span>
          ) : null}
        </CardTitle>
        <div className="flex gap-1.5">
          {(["visit", "job_walk", "warranty"] as const).map((k) => (
            <Button
              key={k}
              variant={adding === k ? "secondary" : "ghost"}
              className="px-2 py-1 text-xs"
              onClick={() => setAdding(adding === k ? null : k)}
            >
              + {KIND_LABEL[k]}
            </Button>
          ))}
        </div>
      </div>

      {adding ? (
        <div className="mt-3 space-y-2 rounded-lg bg-cream p-3">
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label>Type</Label>
              <Select className="w-36 py-1 text-xs" value={visitType} onChange={(e) => setVisitType(e.target.value)}>
                <option value="">—</option>
                {jobTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" className="w-36 py-1 text-xs" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div>
              <Label>Time</Label>
              <Input type="time" className="w-28 py-1 text-xs" value={time} onChange={(e) => setTime(e.target.value)} />
            </div>
            {adding === "warranty" ? (
              <div className="min-w-56 flex-1">
                <Label>Who pays?</Label>
                <Select value={coverage} onChange={(e) => setCoverage(e.target.value as Coverage)}>
                  {(Object.keys(COVERAGE_LABEL) as Coverage[]).map((c) => (
                    <option key={c} value={c}>
                      {COVERAGE_LABEL[c]}
                    </option>
                  ))}
                </Select>
              </div>
            ) : null}
            <Button className="px-3 py-1.5 text-xs" busy={busy} onClick={() => void create()}>
              Add {KIND_LABEL[adding]}
            </Button>
          </div>
          <div>
            <Label>Notes for this {(KIND_LABEL[adding] ?? "visit").toLowerCase()}</Label>
            <Input className="py-1 text-xs" value={visitNote} placeholder="What's this visit for?" onChange={(e) => setVisitNote(e.target.value)} />
          </div>
          {adding === "warranty" && (coverage === "builder" || coverage === "manufacturer") ? (
            <div>
              <Label>Bill to (search customers)</Label>
              <Input
                placeholder="e.g. Toll Brothers"
                className="py-1 text-xs"
                value={billToSearch}
                onChange={(e) => {
                  setBillToSearch(e.target.value);
                  setBillToId(null);
                }}
              />
              {billToMatches.length > 0 && !billToId ? (
                <div className="mt-1 rounded-lg border border-steel/20 bg-white">
                  {billToMatches.map((c) => (
                    <button
                      key={c.id}
                      className="block w-full px-3 py-1.5 text-left text-xs text-ink hover:bg-cream"
                      onClick={() => {
                        setBillToId(c.id);
                        setBillToSearch(c.name);
                      }}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              ) : null}
              {billToId ? <p className="mt-1 text-[11px] text-success">Bill-to set ✓</p> : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mt-2">
        {children.map((c) => (
          <Link
            key={c.id}
            href={`/jobs/${c.id}`}
            className="flex items-center justify-between border-b border-steel/10 py-1.5 text-sm last:border-0 hover:bg-cream/60"
          >
            <span className="min-w-0 truncate text-ink">
              <span className="mr-1.5 rounded-full bg-steel/15 px-1.5 py-0.5 text-[10px] uppercase text-steel">
                {KIND_LABEL[c.kind] ?? c.kind}
              </span>
              {c.title}
              {c.coverage ? <span className="ml-1 text-xs text-steel">· {c.coverage}</span> : null}
              {c.workCount ? (
                <span className="ml-1 text-xs text-steel">
                  · {c.workCount} logged · {money(c.workTotalCents ?? 0)}
                </span>
              ) : null}
            </span>
            <span
              className={cn(
                "ml-2 shrink-0 rounded-full px-2 py-0.5 text-xs",
                c.status === "done"
                  ? "bg-success/15 text-success"
                  : c.status === "in_progress"
                    ? "bg-brandorange/15 text-brandorange-dark"
                    : "bg-steel/15 text-ink",
              )}
            >
              {c.status.replace("_", " ")}
            </span>
          </Link>
        ))}
        {children.length === 0 && !adding ? (
          <p className="py-1 text-xs text-steel">No visits or claims yet — bigger jobs grow them here.</p>
        ) : null}
      </div>
    </Card>
  );
}
